
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const location = useLocation();

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.products'), path: '/products' },
    { name: t('nav.certifications'), path: '/certifications' },
    { name: t('nav.partnerships'), path: '/partnerships' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', displayName: 'EN' },
    { code: 'ms', name: 'Bahasa Malaysia', flag: '🇲🇾', displayName: 'MS' },
    { code: 'zh', name: '中文', flag: '🇨🇳', displayName: 'ZH' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', displayName: 'AR' },
    { code: 'es', name: 'Español', flag: '🇪🇸', displayName: 'ES' },
    { code: 'fr', name: 'Français', flag: '🇫🇷', displayName: 'FR' }
  ];

  const getCurrentLanguageDisplay = () => {
    const current = languages.find(lang => lang.code === i18n.language);
    return current ? current.displayName : 'EN';
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
    // Store language preference in localStorage
    localStorage.setItem('preferred-language', langCode);
    // Force page reload to ensure all translations are updated
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/1912de5b3ab345395afff437b1bfb754.jfif" 
              alt="Drixem Group Malaysia" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-800">{t('company.name')}</h1>
              <p className="text-sm text-gray-600">{t('company.tagline')}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => window.scrollTo(0, 0)}
                className={`font-medium transition-colors duration-200 hover:text-yellow-600 ${
                  location.pathname === item.path ? 'text-yellow-600' : 'text-gray-700'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-medium">{getCurrentLanguageDisplay()}</span>
                <i className={`ri-arrow-${isLanguageOpen ? 'up' : 'down'}-s-line text-sm`}></i>
              </button>
              
              {isLanguageOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full px-4 py-2 text-left flex items-center space-x-3 hover:bg-yellow-50 transition-colors ${
                        i18n.language === lang.code ? 'bg-yellow-50 text-yellow-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Link to="/quote" onClick={() => window.scrollTo(0, 0)}>
              <Button size="sm">{t('nav.quote')}</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl text-gray-700`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.scrollTo(0, 0);
                  }}
                  className={`font-medium transition-colors duration-200 hover:text-yellow-600 ${
                    location.pathname === item.path ? 'text-yellow-600' : 'text-gray-700'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Language Selector */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-700 mb-2">Language</p>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        handleLanguageChange(lang.code);
                        setIsMenuOpen(false);
                      }}
                      className={`px-3 py-2 text-left flex items-center space-x-2 rounded-lg transition-colors ${
                        i18n.language === lang.code ? 'bg-yellow-50 text-yellow-600' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <Link to="/quote" onClick={() => window.scrollTo(0, 0)}>
                <Button size="sm" className="w-fit mt-4">{t('nav.quote')}</Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
